// Availability: everyone's real week, with classes blocked out.
//
// Two layers on one CSS grid. Clickable half-hour cells sit in the normal flow; class
// blocks are placed explicitly with grid-row spans on top. Dragging a block writes
// either to the base schedule ("Typical week") or to that week's override map.

import { C, CLASSES } from "../data/classes.js";
import { P, PEOPLE } from "../data/people.js";
import { addDays, d, fmt, iso, mondayOf, today } from "../lib/dates.js";
import { byId, el, esc } from "../lib/dom.js";
import {
  AV_DAYKEYS,
  AV_DAYS,
  AV_END,
  AV_N,
  blockName,
  busyMap,
  effectiveBlocks,
  hm2m,
  isFreeAt,
  m2hm,
  m2s,
  s2m,
  slotKey,
  tLabel,
} from "../lib/schedule.js";
import { S } from "../lib/state.js";

/** "Typical week" plus the next four dated weeks. */
function weekOptions() {
  const m0 = mondayOf(today());
  const out = [["base", "Typical week"]];
  for (let i = 0; i < 4; i++) {
    const mk = iso(addDays(m0, i * 7));
    out.push([mk, i === 0 ? "This week" : "Wk of " + fmt(mk)]);
  }
  return out;
}

export function moveBlock(schedDoc, bid, day, startSlot, weekKey) {
  const sc = schedDoc || { blocks: [], ex: {} };
  const base = (sc.blocks || []).find((b) => b.id === bid);
  if (!base) return sc;

  const current = effectiveBlocks(sc, weekKey).find((b) => b.id === bid) || base;
  const duration = hm2m(current.end) - hm2m(current.start);
  let start = s2m(startSlot);
  if (start + duration > AV_END) start = AV_END - duration;
  const rec = { day, start: m2hm(start), end: m2hm(start + duration) };

  if (weekKey === "base") {
    base.day = rec.day;
    base.start = rec.start;
    base.end = rec.end;
  } else {
    sc.ex = sc.ex || {};
    sc.ex[weekKey] = sc.ex[weekKey] || {};
    // Dropping it back where it normally lives clears the override rather than
    // storing a no-op exception.
    if (base.day === rec.day && base.start === rec.start) delete sc.ex[weekKey][bid];
    else sc.ex[weekKey][bid] = rec;
    if (!Object.keys(sc.ex[weekKey]).length) delete sc.ex[weekKey];
  }
  return sc;
}

export function removeBlock(schedDoc, bid, weekKey) {
  const sc = schedDoc || { blocks: [], ex: {} };
  const b = (sc.blocks || []).find((x) => x.id === bid);
  if (!b) return null;

  if (weekKey !== "base") {
    if (!confirm(`Cancel ${blockName(b)} for this week only?`)) return null;
    sc.ex = sc.ex || {};
    sc.ex[weekKey] = sc.ex[weekKey] || {};
    sc.ex[weekKey][bid] = null;
  } else {
    if (!confirm(`Remove ${blockName(b)} from your typical week? This is permanent.`)) return null;
    sc.blocks = (sc.blocks || []).filter((x) => x.id !== bid);
    Object.keys(sc.ex || {}).forEach((w) => delete sc.ex[w][bid]);
  }
  return sc;
}

export function renderAddClass(onAdd, rerender) {
  const host = byId("addClassHost");
  host.innerHTML = "";
  if (!S.addingClass) return;

  const times = [];
  for (let s = 0; s < AV_N; s++) times.push([m2hm(s2m(s)), tLabel(s2m(s))]);

  const f = el("div", "editor");
  f.innerHTML = `
    <div class="frow">
      <div class="field"><label for="a-cls">Class</label><select id="a-cls">
        ${CLASSES.map((c) => `<option value="${c.id}">${c.id} ${esc(c.name)}</option>`).join("")}
      </select></div>
      <div class="field"><label for="a-label">Label <span style="text-transform:none;letter-spacing:0">(optional)</span></label>
        <input id="a-label" type="text" size="12" placeholder="e.g. Comm Lab"></div>
      <div class="field"><label for="a-day">Day</label><select id="a-day">
        ${AV_DAYS.map(([k, l]) => `<option value="${k}">${l}</option>`).join("")}
      </select></div>
      <div class="field"><label for="a-start">Starts</label><select id="a-start">
        ${times.map(([v, l]) => `<option value="${v}"${v === "1000" ? " selected" : ""}>${l}</option>`).join("")}
      </select></div>
      <div class="field"><label for="a-dur">Length</label><select id="a-dur">
        ${[[60, "1 hour"], [90, "1.5 hours"], [120, "2 hours"], [180, "3 hours"], [330, "5.5 hours"]]
          .map(([v, l]) => `<option value="${v}"${v === 90 ? " selected" : ""}>${l}</option>`)
          .join("")}
      </select></div>
      <div class="field"><label for="a-room">Room</label><input id="a-room" type="text" size="9" placeholder="E62-262"></div>
      <button class="btn ghost" id="a-cancel">Cancel</button>
      <button class="btn" id="a-save">Add to my week</button>
    </div>
    <div style="font-size:11.5px;color:var(--ink-3)">Adds to your typical week. Drag it later if a particular week shifts.</div>`;
  host.appendChild(f);

  f.querySelector("#a-cancel").onclick = () => {
    S.addingClass = false;
    renderAddClass(onAdd, rerender);
  };
  f.querySelector("#a-save").onclick = () => {
    const start = f.querySelector("#a-start").value;
    const dur = +f.querySelector("#a-dur").value;
    onAdd({
      id: "b" + Date.now().toString(36) + Math.random().toString(36).slice(2, 5),
      cls: f.querySelector("#a-cls").value,
      label: f.querySelector("#a-label").value.trim(),
      day: f.querySelector("#a-day").value,
      start,
      end: m2hm(Math.min(AV_END, hm2m(start) + dur)),
      room: f.querySelector("#a-room").value.trim(),
    });
    S.addingClass = false;
    renderAddClass(onAdd, rerender);
    rerender();
  };
}

/**
 * @param {object} avail  availability snapshot, keyed by person id
 * @param {object} sched  schedule snapshot, keyed by person id
 */
export function render(avail, sched) {
  byId("availWho").textContent = P[S.me].name;

  const modeBtn = byId("availMode");
  modeBtn.setAttribute("aria-pressed", S.availOverlap ? "true" : "false");
  modeBtn.textContent = S.availOverlap ? "Back to my schedule" : "Show team overlap";

  const picker = byId("weekPick");
  picker.innerHTML = "";
  weekOptions().forEach(([key, label]) => {
    const b = el("button", null, esc(label));
    b.setAttribute("aria-pressed", S.weekKey === key ? "true" : "false");
    b.onclick = () => {
      S.weekKey = key;
      render(avail, sched);
    };
    picker.appendChild(b);
  });

  const overrides =
    S.weekKey !== "base" && sched[S.me] && sched[S.me].ex && sched[S.me].ex[S.weekKey]
      ? Object.keys(sched[S.me].ex[S.weekKey]).length
      : 0;
  byId("weekReset").hidden = !overrides;

  const busy = Object.fromEntries(PEOPLE.map((p) => [p.id, busyMap(sched[p.id], S.weekKey)]));
  const freeAt = (pid, day, s) => isFreeAt(avail[pid], busy[pid], day, s);

  const grid = byId("wkGrid");
  grid.innerHTML = "";
  const t = today();
  const weekStart = S.weekKey === "base" ? null : d(S.weekKey);
  const mySlots = (avail[S.me] && avail[S.me].slots) || [];

  grid.appendChild(el("div", "hd"));
  AV_DAYS.forEach(([, label], i) => {
    const h = el("div", "hd dcol");
    let text = label;
    if (weekStart) {
      const dt = addDays(weekStart, i);
      if (iso(dt) === iso(t)) h.className += " istoday";
      text = `${label}<span class="dd">${dt.getDate()}</span>`;
    }
    h.innerHTML = text;
    grid.appendChild(h);
  });

  for (let s = 0; s < AV_N; s++) {
    const mins = s2m(s);
    if (mins % 60 === 0) {
      const label = el("div", "tl", tLabel(mins));
      label.style.gridRow = `${s + 2} / span 2`;
      grid.appendChild(label);
    }
    AV_DAYS.forEach(([day, dayLabel], i) => {
      const cell = el("button", "slot" + (mins % 60 === 0 ? " hourline" : "") + (i > 4 ? " wknd" : ""));
      cell.style.gridColumn = i + 2;
      cell.style.gridRow = s + 2;
      if (S.availOverlap) {
        cell.disabled = true;
      } else if (busy[S.me][day + "-" + s]) {
        cell.className += " busy";
        cell.disabled = true;
        cell.setAttribute("data-drop", `${day}|${s}`);
      } else {
        if (mySlots.includes(slotKey(day, s))) cell.className += " free";
        cell.setAttribute("data-slot2", `${day}|${s}`);
        cell.setAttribute("data-drop", `${day}|${s}`);
        cell.title = `${dayLabel} ${tLabel(mins)}`;
      }
      grid.appendChild(cell);
    });
  }

  if (S.availOverlap) {
    AV_DAYS.forEach(([day], i) => {
      for (let s = 0; s < AV_N; s++) {
        const who = PEOPLE.filter((p) => freeAt(p.id, day, s));
        if (!who.length) continue;
        const o = el("div", "ovl h" + who.length, who.length >= 3 ? String(who.length) : "");
        o.style.gridColumn = i + 2;
        o.style.gridRow = s + 2;
        o.title = `${tLabel(s2m(s))} — free: ${who.map((p) => p.short).join(", ")}`;
        grid.appendChild(o);
      }
    });
  } else {
    effectiveBlocks(sched[S.me], S.weekKey).forEach((b) => {
      const i = AV_DAYKEYS.indexOf(b.day);
      if (i < 0) return;
      const a = m2s(hm2m(b.start));
      const z = m2s(hm2m(b.end));
      const c = C[b.cls] || { v: "--line-strong", s: "--surface-2" };
      const blk = el("div", "blk" + (b.moved ? " moved" : ""));
      blk.style.gridColumn = i + 2;
      blk.style.gridRow = `${a + 2} / span ${Math.max(1, z - a)}`;
      blk.style.background = `var(${c.s})`;
      blk.style.borderLeftColor = `var(${c.v})`;
      blk.style.color = `var(${c.v})`;
      blk.draggable = true;
      blk.setAttribute("data-blk", b.id);
      blk.title =
        `${C[b.cls] ? C[b.cls].full : b.cls} · ${tLabel(hm2m(b.start))}–${tLabel(hm2m(b.end))}` +
        (b.room ? ` · ${b.room}` : "") +
        (b.moved ? " · moved for this week" : "") +
        " — drag to move";
      blk.innerHTML =
        `<span class="bt">${esc(blockName(b))}</span>` +
        (z - a > 1 ? `<span class="bm">${tLabel(hm2m(b.start))}${b.room ? " · " + esc(b.room) : ""}</span>` : "") +
        `<button class="bx" data-delblk="${esc(b.id)}" title="Remove">×</button>`;
      grid.appendChild(blk);
    });
  }

  byId("avHint").innerHTML = S.availOverlap
    ? "<span>Darker green = more of you free at once. Everyone’s classes are already removed.</span>"
    : '<span><i style="background:var(--good-soft);border-color:var(--good)"></i>Your free blocks</span>' +
      '<span><i style="background:var(--c060s);border-left:3px solid var(--c060)"></i>Class — drag to move</span>' +
      (S.weekKey === "base"
        ? "<span>Editing your <b>typical week</b>. Switch to a dated week to move a class just once.</span>"
        : `<span>Moves here apply to <b>this week only</b>.${overrides ? ` ${overrides} block${overrides === 1 ? "" : "s"} moved.` : ""}</span>`);

  renderBestSlots(freeAt);
}

/** Hour-long windows where at least three people are free, best first, non-overlapping. */
function renderBestSlots(freeAt) {
  const candidates = [];
  AV_DAYS.forEach(([day, dayLabel]) => {
    for (let s = 0; s + 2 <= AV_N; s++) {
      const first = PEOPLE.filter((p) => freeAt(p.id, day, s)).map((p) => p.id);
      if (first.length < 3) continue;
      const second = PEOPLE.filter((p) => freeAt(p.id, day, s + 1)).map((p) => p.id);
      const both = first.filter((x) => second.includes(x));
      if (both.length < 3) continue;
      candidates.push({ day, dayLabel, s, who: both });
    }
  });

  candidates.sort(
    (a, b) =>
      b.who.length - a.who.length ||
      AV_DAYKEYS.indexOf(a.day) - AV_DAYKEYS.indexOf(b.day) ||
      a.s - b.s
  );

  const taken = {};
  const picked = [];
  for (const x of candidates) {
    if (picked.length >= 8) break;
    if (taken[`${x.day}-${x.s}`] || taken[`${x.day}-${x.s - 1}`]) continue;
    taken[`${x.day}-${x.s}`] = true;
    taken[`${x.day}-${x.s + 1}`] = true;
    picked.push(x);
  }

  const host = byId("bestSlots");
  host.innerHTML = "";
  if (!picked.length) {
    host.appendChild(
      el(
        "div",
        "empty",
        "No hour yet where three or more of you are free. Mark your own free blocks above — the overlaps land here as people fill theirs in."
      )
    );
    return;
  }

  picked.forEach((x) => {
    const missing = PEOPLE.filter((p) => !x.who.includes(p.id)).map((p) => p.short);
    const row = el("div", "slotrow");
    row.innerHTML =
      `<span class="when">${x.dayLabel} ${tLabel(s2m(x.s))}–${tLabel(s2m(x.s + 2))}</span>` +
      `<span class="who">${missing.length ? "missing " + missing.join(", ") : "everyone free"}</span>` +
      `<span class="cnt">${x.who.length} of 6</span>`;
    host.appendChild(row);
  });
}
